import { getReactiveRequest, rejectReactiveRequest, resolveReactiveRequest } from '../queue';
import { calculateDuration } from '../utils';

/**
 * DocumentInitializationHandler - Utility for handling document initialization completion messages
 *
 * Processes document initialization completion messages from the Typesetter Web Worker,
 * including timing calculations, error handling, and state updates for successful initialization.
 */
export class DocumentInitializationHandler {
  /**
   * Handle document initialization completion from worker
   *
   * This method processes initialization completion messages by:
   * 1. Finding the corresponding reactive request
   * 2. Calculating operation duration
   * 3. Handling errors by rejecting the request
   * 4. Updating initialization state and resolving the request on success
   *
   * @param msg - Raw message payload from worker
   * @param reactiveRequests - Map of layout IDs to reactive request handlers
   * @param documentInitialized - Reference to document initialization state
   */
  static handleInitializeDocumentComplete(
    msg: any,
    reactiveRequests: Map<any, {
      resolve: (result: any) => void;
      reject: (error: Error) => void;
      timeout: NodeJS.Timeout;
      startTime: number;
      operation: string;
    }>,
    documentInitialized: { current: boolean }
  ): void {
    const { layoutId, version, error } = msg;

    console.log(`📥 [DocumentInitializationHandler] initializeDocumentComplete (layoutId=${layoutId}, version=${version})`);

    const request = getReactiveRequest(reactiveRequests, layoutId);
    if (!request) {
      console.warn(`⚠️ [DocumentInitializationHandler] No pending request for layoutId ${layoutId}`);
      return;
    }

    const duration = calculateDuration(request.startTime || 0, Date.now());
    console.log(`✅ [DocumentInitializationHandler] ${request.operation} completed in ${duration}ms`);

    if (error) {
      rejectReactiveRequest(reactiveRequests, layoutId, error);
    } else {
      resolveReactiveRequest(reactiveRequests, layoutId, { version });
      documentInitialized.current = true;
    }
  }
}
